import { axiosInstance } from './axiosInstance';
import { progressFromMultipartUpload } from '../components/common/FileTransferProgressDialog';
import { getStoredRefreshToken, refreshAccessToken } from './authTokenRefresh';
export type FacilityBoardTargetMode = 'all_pools' | 'pools' | 'central';

export interface FacilityBoardAttachment {
  id: number;
  original_name: string;
  content_type?: string | null;
  size_bytes: number;
  created_at: string;
}

export interface FacilityBoardRecipientStatus {
  recipient_kind: 'central' | 'pool';
  pool_id?: number | null;
  pool_name?: string | null;
  label: string;
  is_read: boolean;
  read_at?: string | null;
}

export interface FacilityBoardMessage {
  id: number;
  title: string;
  body: string;
  sender_kind: string;
  sender_pool_id?: number | null;
  sender_pool_name?: string | null;
  sender_label: string;
  author_user_id?: number | null;
  author_username?: string | null;
  author_full_name?: string | null;
  target_all_pools: boolean;
  recipient_labels: string[];
  is_own_sent?: boolean;
  attachment_count: number;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  can_edit: boolean;
  can_delete?: boolean;
}

export interface FacilityBoardMessageDetail extends FacilityBoardMessage {
  attachments: FacilityBoardAttachment[];
  recipient_status: FacilityBoardRecipientStatus[];
}

const BASE = '/api/facility-board';

function poolParams(poolId?: number | null) {
  return poolId != null ? { pool_id: poolId } : undefined;
}

export const facilityBoardService = {
  async list(poolId?: number | null): Promise<{ items: FacilityBoardMessage[]; total: number }> {
    const res = await axiosInstance.get<{ items: FacilityBoardMessage[]; total: number }>(BASE, {
      params: poolParams(poolId),
    });
    return res.data;
  },

  async listUnread(poolId?: number | null): Promise<FacilityBoardMessage[]> {
    const res = await axiosInstance.get<FacilityBoardMessage[]>(`${BASE}/unread`, {
      params: poolParams(poolId),
    });
    return Array.isArray(res.data) ? res.data : [];
  },

  async getDetail(id: number, poolId?: number | null): Promise<FacilityBoardMessageDetail> {
    const res = await axiosInstance.get<FacilityBoardMessageDetail>(`${BASE}/${id}`, {
      params: poolParams(poolId),
    });
    return res.data;
  },

  async create(payload: {
    title: string;
    body: string;
    target_mode: FacilityBoardTargetMode;
    target_pool_ids?: number[];
    sender_pool_id?: number | null;
    files?: File[];
    onUploadProgress?: (percent: number | null) => void;
    onUploadFileProgress?: (info: {
      overallPercent: number | null;
      filePercent: number | null;
      fileIndex: number;
      fileCount: number;
      currentFilename: string;
    }) => void;
  }): Promise<FacilityBoardMessageDetail> {
    const form = new FormData();
    form.append('title', payload.title);
    form.append('body', payload.body);
    form.append('target_mode', payload.target_mode);
    if (payload.target_pool_ids?.length) {
      form.append('target_pool_ids', JSON.stringify(payload.target_pool_ids));
    }
    if (payload.sender_pool_id != null) {
      form.append('sender_pool_id', String(payload.sender_pool_id));
    }
    const files = (payload.files || []).slice(0, 5);
    if (files.length > 0 && getStoredRefreshToken()) {
      try {
        await refreshAccessToken();
      } catch {
        /* ignore */
      }
    }
    files.forEach((f) => form.append('files', f));
    // axios 기본 timeout 30초·JSON Content-Type 은 대용량 첨부에 실패함
    const res = await axiosInstance.post<FacilityBoardMessageDetail>(BASE, form, {
      timeout: 0,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress: (ev) => {
        if (payload.onUploadFileProgress && files.length > 0) {
          const info = progressFromMultipartUpload(files, ev.loaded, ev.total);
          payload.onUploadFileProgress(info);
          payload.onUploadProgress?.(info.overallPercent);
        } else if (payload.onUploadProgress) {
          if (ev.total && ev.total > 0) {
            payload.onUploadProgress(Math.round((ev.loaded * 100) / ev.total));
          } else {
            payload.onUploadProgress(null);
          }
        }
      },
      transformRequest: [
        (data, headers) => {
          if (typeof FormData !== 'undefined' && data instanceof FormData && headers) {
            delete (headers as Record<string, unknown>)['Content-Type'];
            delete (headers as Record<string, unknown>)['content-type'];
          }
          return data;
        },
      ],
    });
    return res.data;
  },

  async downloadAttachment(
    attachmentId: number,
    filename: string,
    poolId?: number | null,
    onDownloadProgress?: (percent: number | null) => void
  ): Promise<void> {
    const res = await axiosInstance.get(`${BASE}/attachments/${attachmentId}/download`, {
      params: poolParams(poolId),
      responseType: 'blob',
      timeout: 0,
      onDownloadProgress: (ev) => {
        if (!onDownloadProgress) return;
        if (ev.total && ev.total > 0) {
          onDownloadProgress(Math.round((ev.loaded * 100) / ev.total));
        } else {
          onDownloadProgress(null);
        }
      },
    });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  async update(
    id: number,
    payload: { title?: string; body?: string },
    poolId?: number | null
  ): Promise<FacilityBoardMessageDetail> {
    const res = await axiosInstance.patch<FacilityBoardMessageDetail>(`${BASE}/${id}`, payload, {
      params: poolParams(poolId),
    });
    return res.data;
  },

  async deleteAttachment(
    attachmentId: number,
    poolId?: number | null
  ): Promise<FacilityBoardMessageDetail> {
    const res = await axiosInstance.delete<FacilityBoardMessageDetail>(
      `${BASE}/attachments/${attachmentId}`,
      { params: poolParams(poolId) }
    );
    return res.data;
  },

  async addAttachments(
    messageId: number,
    files: File[],
    poolId?: number | null,
    opts?: {
      onUploadProgress?: (percent: number | null) => void;
      onUploadFileProgress?: (info: {
        overallPercent: number | null;
        filePercent: number | null;
        fileIndex: number;
        fileCount: number;
        currentFilename: string;
      }) => void;
    }
  ): Promise<FacilityBoardMessageDetail> {
    const list = (files || []).slice(0, 5);
    if (list.length === 0) {
      return facilityBoardService.getDetail(messageId, poolId);
    }
    if (getStoredRefreshToken()) {
      try {
        await refreshAccessToken();
      } catch {
        /* ignore */
      }
    }
    const form = new FormData();
    list.forEach((f) => form.append('files', f));
    const res = await axiosInstance.post<FacilityBoardMessageDetail>(
      `${BASE}/${messageId}/attachments`,
      form,
      {
        params: poolParams(poolId),
        timeout: 0,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        onUploadProgress: (ev) => {
          if (opts?.onUploadFileProgress && list.length > 0) {
            const info = progressFromMultipartUpload(list, ev.loaded, ev.total);
            opts.onUploadFileProgress(info);
            opts.onUploadProgress?.(info.overallPercent);
          } else if (opts?.onUploadProgress) {
            if (ev.total && ev.total > 0) {
              opts.onUploadProgress(Math.round((ev.loaded * 100) / ev.total));
            } else {
              opts.onUploadProgress(null);
            }
          }
        },
        transformRequest: [
          (data, headers) => {
            if (typeof FormData !== 'undefined' && data instanceof FormData && headers) {
              delete (headers as Record<string, unknown>)['Content-Type'];
              delete (headers as Record<string, unknown>)['content-type'];
            }
            return data;
          },
        ],
      }
    );
    return res.data;
  },

  async remove(id: number, poolId?: number | null): Promise<void> {
    await axiosInstance.delete(`${BASE}/${id}`, {
      params: poolParams(poolId),
    });
  },

  async markRead(id: number, poolId?: number | null): Promise<void> {
    await axiosInstance.post(`${BASE}/${id}/read`, null, { params: poolParams(poolId) });
  },

  attachmentDownloadUrl(attachmentId: number, poolId?: number | null): string {
    const qs =
      poolId != null ? `?pool_id=${encodeURIComponent(String(poolId))}` : '';
    return `${BASE}/attachments/${attachmentId}/download${qs}`;
  },
};
